import { DEFAULT_FLEET_STATUS_FILTER, FleetStatusFilterOption } from "enums/FleetEnums";
import { useQueryParam } from "hooks/useQueryParam";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	getFiltersFromConfig,
	loadGridConfig,
	saveGridConfig,
	updateFiltersInConfig
} from "utils/gridConfig";

const CONFIG_PATH = ["personnelAndFleet", "fleet"];

type FleetFilters = { status: string };

export function useFleetQueryParams() {
	const [, setSearchParams] = useSearchParams();

	// Local state: seeded from URL if present on mount, otherwise from localStorage.
	// Kept separate from the URL param so personnel's shared "status" param can't interfere.
	const [fleetStatus, setFleetStatusState] = useState<FleetStatusFilterOption>(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has("status")) {
			return params.get("status") as FleetStatusFilterOption;
		}
		const saved = getFiltersFromConfig<FleetFilters>(loadGridConfig(), CONFIG_PATH);
		return (saved?.status as FleetStatusFilterOption) ?? DEFAULT_FLEET_STATUS_FILTER;
	});

	const [, setFleetStatusParam] = useQueryParam<FleetStatusFilterOption>(
		"status",
		DEFAULT_FLEET_STATUS_FILTER
	);
	const setFleetStatus = useCallback(
		(status: FleetStatusFilterOption) => {
			setFleetStatusState(status);
			setFleetStatusParam(status);
			saveGridConfig(updateFiltersInConfig(loadGridConfig(), CONFIG_PATH, { status }));
		},
		[setFleetStatusParam]
	);
	const [searchFilter, setSearchFilter] = useQueryParam<string>("search", "");

	const applyFilters = useCallback(
		(status: FleetStatusFilterOption, search: string) => {
			setFleetStatusState(status);
			saveGridConfig(updateFiltersInConfig(loadGridConfig(), CONFIG_PATH, { status }));
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (status === DEFAULT_FLEET_STATUS_FILTER) {
						next.delete("status");
					} else {
						next.set("status", status);
					}
					if (!search) {
						next.delete("search");
					} else {
						next.set("search", search);
					}
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	return { fleetStatus, setFleetStatus, searchFilter, setSearchFilter, applyFilters };
}
